import { CircularProgress } from '@material-ui/core';
import { Check as CheckIcon } from '@material-ui/icons';
import React from 'react';

/**
 * The frame every onboarding step sits in: named steps across the top, one
 * heading and a line of context, the step's content, and an action bar fixed to
 * the bottom of the screen so Continue never scrolls out of reach. Shares its
 * look with the standalone tour picker (TourSelectionGate) so moving from one
 * to the other does not feel like changing product.
 */

export const Stepper = ({ steps, activeKey }) => {
  const activeIndex = steps.findIndex(step => step.key === activeKey);
  return (
    <ol className="list-none m-0 p-0 flex items-center gap-2" aria-label="Setup steps">
      {steps.map((step, index) => {
        const done = index < activeIndex;
        const current = index === activeIndex;
        return (
          <li key={step.key} className="flex items-center gap-2 min-w-0" aria-current={current ? 'step' : undefined}>
            {index > 0 && (
              <span
                aria-hidden="true"
                className={`block w-6 sm:w-10 h-px ${done || current ? 'bg-indigo-300' : 'bg-slate-300'}`}
              />
            )}
            <span
              className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0 ${
                done
                  ? 'bg-indigo-600 text-white'
                  : current
                  ? 'bg-white text-indigo-700 ring-2 ring-indigo-600'
                  : 'bg-white text-slate-500 ring-1 ring-slate-300'
              }`}>
              {done ? <CheckIcon style={{ fontSize: 14 }} /> : index + 1}
            </span>
            <span
              className={`text-sm truncate ${
                current ? 'font-semibold text-slate-900' : done ? 'text-slate-700' : 'text-slate-500'
              } ${current ? '' : 'hidden sm:inline'}`}>
              {step.label}
              {done && <span className="sr-only"> (done)</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const primaryClass =
  'shrink-0 min-w-[140px] h-11 px-6 rounded-xl border-0 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white text-sm font-semibold cursor-pointer transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-default disabled:active:scale-100 flex items-center justify-center';
const textButtonClass =
  'bg-transparent border-0 p-0 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-2 cursor-pointer disabled:text-slate-400 disabled:cursor-default';

const OnboardingShell = ({
  steps,
  activeKey,
  title,
  description,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  busy = false,
  onBack,
  onSkip,
  status,
  error
}) => (
  <div className="flex flex-col w-full bg-slate-50 h-[calc(100%-77px)] md:h-[calc(100%-93px)]" data-testid="onboarding">
    <div className="flex-1 overflow-y-auto">
      <div className="w-full max-w-[600px] mx-auto px-4 pt-6 md:pt-10 pb-8">
        <Stepper steps={steps} activeKey={activeKey} />
        <h1 className="text-2xl md:text-3xl font-black text-slate-900 m-0 mt-8">{title}</h1>
        {description && (
          <p className="text-base text-slate-600 leading-relaxed mt-2 mb-0 max-w-[56ch]">{description}</p>
        )}
        <div className="mt-7">{children}</div>
        <p className="text-xs text-slate-500 mt-10 mb-0">
          By continuing to use this product, you accept our{' '}
          <a
            href="https://tripadmit.com/tos/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-700 underline">
            Terms of Service
          </a>
          .
        </p>
      </div>
    </div>

    <div className="border-0 border-t border-solid border-slate-200 bg-white">
      <div className="w-full max-w-[600px] mx-auto px-4 py-3 flex items-center gap-4">
        <div className="flex-1 min-w-0 flex items-center gap-4">
          {onBack && (
            <button type="button" className={textButtonClass} onClick={onBack} disabled={busy}>
              Back
            </button>
          )}
          {error ? (
            <span className="text-xs text-red-700" role="alert">
              {error}
            </span>
          ) : (
            status && (
              <span className="text-sm font-semibold text-slate-900" aria-live="polite">
                {status}
              </span>
            )
          )}
        </div>
        {onSkip && (
          <button type="button" className={textButtonClass} onClick={onSkip} disabled={busy}>
            Skip for now
          </button>
        )}
        <button
          type="button"
          className={primaryClass}
          onClick={onPrimary}
          disabled={primaryDisabled || busy}
          data-testid="onboarding-primary">
          {busy ? <CircularProgress size={20} color="inherit" /> : primaryLabel}
        </button>
      </div>
    </div>
  </div>
);

export default OnboardingShell;
